import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'About', path: '/about' },
        { name: 'Services', path: '/services' },
        { name: 'Digital Assets', path: '/#digital-assets', hash: true },
        { name: 'Contact', path: '/contact' },
    ];

    const isActive = (path) => location.pathname === path;

    const handleNavClick = (link, e) => {
        if (link.hash) {
            e.preventDefault();
            // If already on home, scroll to the section
            if (location.pathname === '/') {
                const el = document.getElementById('digital-assets');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
            } else {
                // Navigate to home with hash
                window.location.href = link.path;
            }
            setIsMenuOpen(false);
        }
    };

    return (
        <nav className="bg-[#3C3B6E]/95 backdrop-blur-md fixed top-0 left-0 right-0 z-50 border-b border-white/20" data-testid="navbar">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 md:h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                        <div className="w-10 h-10 bg-[#B22234] rounded-lg flex items-center justify-center">
                            <Globe className="w-6 h-6 text-white" />
                        </div>
                        <span className="font-black text-xl tracking-tight text-white">
                            BIONEXX LLC
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                onClick={(e) => handleNavClick(link, e)}
                                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`text-sm font-medium transition-colors ${
                                    isActive(link.path)
                                        ? 'text-[#B22234]'
                                        : 'text-white hover:text-[#B22234]'
                                }`}
                            >
                                {link.name}
                            </Link>
                        ))}
                        <Link to="/contact">
                            <Button 
                                className="bg-[#B22234] hover:bg-[#8B1A1A] text-white rounded-md px-6 font-semibold"
                                data-testid="nav-cta-button"
                            >
                                Get a Quote
                            </Button>
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden p-2"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        data-testid="mobile-menu-button"
                        aria-label="Toggle menu"
                    >
                        {isMenuOpen ? (
                            <X className="w-6 h-6 text-white" />
                        ) : (
                            <Menu className="w-6 h-6 text-white" />
                        )}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div className="md:hidden bg-[#3C3B6E] border-t border-white/20" data-testid="mobile-menu">
                    <div className="px-4 py-4 space-y-3">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                onClick={(e) => {
                                    if (link.hash) {
                                        handleNavClick(link, e);
                                    } else {
                                        setIsMenuOpen(false);
                                    }
                                }}
                                data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`block py-2 text-base font-medium ${
                                    isActive(link.path)
                                        ? 'text-[#B22234]'
                                        : 'text-white'
                                }`}
                            >
                                {link.name}
                            </Link>
                        ))}
                        <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                            <Button 
                                className="w-full bg-[#B22234] hover:bg-[#8B1A1A] text-white rounded-md mt-2 font-semibold"
                                data-testid="mobile-nav-cta-button"
                            >
                                Get a Quote
                            </Button>
                        </Link>
                    </div>
                </div>
            )}
        </nav>
    );
};
